import type { API, BlockAPI } from '@editorjs/editorjs';
import type { ReactNode } from 'react';

import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { EMPTY_OBJECT } from '../../../../../lib/globals';
import { getTestProps } from '../../../../../lib/helpers';

export interface FlotiqImageTune {
  withBorder?: boolean;
  withBackground?: boolean;
  stretched?: boolean;
}

export interface FlotiqImageSetting {
  name: keyof FlotiqImageTune;
  title: string;
  icon: ReactNode;
}

export type FlotiqImageToggleTuneFn = (
  name: FlotiqImageSetting['name'],
) => void;

type SettingsButtonOnClickFn = (name: FlotiqImageSetting['name']) => void;

interface SettingsButtonProps extends FlotiqImageSetting {
  onClick: SettingsButtonOnClickFn;
  styles: {
    settingsButton: string;
    settingsButtonActive: string;
  };
  isActive?: boolean;
  testId?: string;
}

const SettingsButton = ({
  name,
  icon,
  onClick,
  styles,
  isActive,
  testId,
}: SettingsButtonProps) => {
  const [active, setActive] = useState(isActive);

  const onClickHandler = useCallback(() => {
    onClick(name);
    setActive((prevState) => !prevState);
  }, [onClick, name]);

  return (
    <button
      className={twMerge(
        styles.settingsButton,
        active && styles.settingsButtonActive,
      )}
      onClick={onClickHandler}
      type="button"
      {...getTestProps(testId, `image-${name}-tune`)}
    >
      {icon}
    </button>
  );
};

interface FlotiqImagePluginSettingsProps {
  /**
   *  available tunes settings
   */
  settings: FlotiqImageSetting[];
  /**
   * toggle image tune callback
   */
  toggleTune: FlotiqImageToggleTuneFn;
  /**
   * Editor js api object
   */
  api: API;
  /**
   * Selected tune data
   */
  tuneData?: FlotiqImageTune;
  /**
   * Editor js block object
   */
  block: BlockAPI;
  /**
   * test id for image plugin settings
   */
  testId?: string;
}

const FlotiqImagePluginSettings = ({
  settings,
  toggleTune,
  api,
  tuneData = EMPTY_OBJECT,
  block,
  testId = '',
}: FlotiqImagePluginSettingsProps) => {
  const handleOnClick = useCallback<SettingsButtonOnClickFn>(
    (name) => {
      toggleTune(name);
      block.dispatchChange();
    },
    [toggleTune, block],
  );

  return (
    <div
      className="flex space-x-2"
      {...getTestProps(testId, 'image-tune-container')}
    >
      {settings.map((element) => {
        return (
          <SettingsButton
            {...element}
            key={element.name}
            onClick={handleOnClick}
            styles={api.styles}
            isActive={tuneData[element.name] || false}
            testId={testId}
          />
        );
      })}
    </div>
  );
};

export default FlotiqImagePluginSettings;
